import {PropsWithChildren} from "react";
import {Alert, Button} from "antd";
import {useProfileStatus} from "../../../../../features/profile-status";
import {
  TConnectedBankApiResponse,
  useGetConnectedBankAccount,
} from "../../../../../react-query-hooks/payout";
import {createContext} from "../../../../../context/create-context";
import {isEmptyConnectedBankAccountMessage} from "../../../../../features/bank-accounts/utils/empty-connected-bank-error";

type TFetchAccountDetailsState = {
  accountDetails: TConnectedBankApiResponse | undefined;
  isStripe: boolean;
  isStripeAndInactive: boolean;
  isStripeAndActive: boolean;
};

type TFetchAccountDetailsContextState = {
  state: TFetchAccountDetailsState;
};

const [useContext, ContextProvider] = createContext<TFetchAccountDetailsContextState>();

export const useFetchAccountDetailsContext = useContext;

export const FetchAccountDetailsProvider = ({children}: PropsWithChildren) => {
  const profileStatusQuery = useProfileStatus();
  const fetchAccountDetailsQuery = useGetConnectedBankAccount({
    connectBankUniqueIdentifier: "account_details_provider",
  });
  const hasNoConnectedBank =
    fetchAccountDetailsQuery.isError &&
    isEmptyConnectedBankAccountMessage(fetchAccountDetailsQuery.error?.message);

  if (profileStatusQuery.isLoading) {
    return <>Loading....</>;
  }

  if (profileStatusQuery.isError) {
    return (
      <Alert
        message="Error"
        description={
          profileStatusQuery.error?.message ?? "Could not load profile status."
        }
        type="error"
        showIcon
        action={
          <Button
            size="small"
            type="primary"
            loading={profileStatusQuery.isFetching}
            onClick={() => void profileStatusQuery.refetch()}
          >
            Retry
          </Button>
        }
      />
    );
  }

  if (fetchAccountDetailsQuery.isLoading) {
    return <>Loading....</>;
  }

  if (fetchAccountDetailsQuery.isError && !hasNoConnectedBank) {
    return (
      <Alert
        message="Error"
        description={
          fetchAccountDetailsQuery.error?.message ?? "Could not load bank details."
        }
        type="error"
        showIcon
        action={
          <Button
            size="small"
            type="primary"
            loading={fetchAccountDetailsQuery.isFetching}
            onClick={() => void fetchAccountDetailsQuery.refetch()}
          >
            Retry
          </Button>
        }
      />
    );
  }

  const accountDetails: TConnectedBankApiResponse | undefined = hasNoConnectedBank
    ? undefined
    : fetchAccountDetailsQuery.data;

  const isStripe =
    !!accountDetails && accountDetails.bank.provider.toLowerCase() === "stripe";
  const isStripeAndInactive =
    isStripe && accountDetails.bank.statusName.toLowerCase() === "inactive";
  const isStripeAndActive =
    isStripe && accountDetails.bank.statusName.toLowerCase() === "active";

  const state: TFetchAccountDetailsState = {
    accountDetails,
    isStripe,
    isStripeAndInactive,
    isStripeAndActive,
  };

  return <ContextProvider value={{state}}>{children}</ContextProvider>;
};
